
// Libraries
import { defineComponent } from 'vue';
import { get } from 'lodash';
import Cookies from 'js-cookie';

// Components
import Access from 'client/containers/user/login/jumpcloudGo/Access.vue';
import AuthView from 'client/containers/user/Views/AuthView.vue';
import ButtonLoginSwitch from 'client/components/ButtonLoginSwitch.vue';
import DuoRedirectHandler from 'client/containers/user/login/mfa/DuoRedirectHandler.vue';
import EmailEntry from 'client/containers/user/login/EmailEntry.vue';
import HelpdeskInfo from 'client/containers/user/login/HelpdeskInfo.vue';
import JumpcloudGoPageLoader from 'client/components/JumpcloudGoPageLoader.vue';
import MfaView from 'client/containers/user/Views/MfaView.vue';
import OtpUserLoginHandler from 'client/containers/user/login/mfa/OtpUserLoginHandler.vue';
import PasswordEntry from 'client/containers/user/login/PasswordEntry.vue';
import PushUserLoginHandler from 'client/containers/user/login/mfa/PushUserLoginHandler.vue';
import Register from 'client/containers/user/login/jumpcloudGo/Register.vue';
import SSOGoContinue from 'client/containers/user/login/SSOGoContinue.vue';
import TotpUserLoginHandler from 'client/containers/user/login/mfa/TotpUserLoginHandler.vue';
import WebAuthnUserLoginHander from 'client/containers/user/login/mfa/WebAuthnUserLoginHandler.vue';

// Constants and Types
import {
  SMS_OTP_IDLE_TIMEOUT_STATUS_CODE,
  SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE,
  createSmsOtpLoginSession,
} from 'client/util/Constants/smsOtpLoginSession';
import { useLoginLabels } from 'client/util/Constants/LoginLabels';
import { useMfaLabels } from 'client/util/Constants/MfaLabels';
import Constants, { LocalStorageConsts, LoginConsts } from 'client/util/Constants';
import MfaConstants from 'client/containers/Login/MfaConstants';
import MfaRequiredError, { MfaFactorResponse } from 'client/services/response/MfaRequiredError';
import RequestError from 'client/services/response/RequestError';

// Utils
import { datadogRum } from '@datadog/browser-rum-slim';
import { durtApiResponseType, extensionTypes, infoResponse } from 'client/services/durtConstants';
import LocalStorageService from 'client/util/LocalStorageService';
import Util from 'client/util/Util';
import api from 'client/api';
import durtService from 'client/services/durtService';
import getCurrentComponentNameFromList from 'client/containers/user/MfaComponents/mfaUtils/getCurrentComponentNameFromList';
import getLastUsedFactor from 'client/containers/user/MfaComponents/mfaUtils/getLastUsedFactor';
import managedBrowserService from 'client/services/managedBrowserService';
import mfaFactorBuilder from 'client/containers/user/MfaComponents/mfaUtils/mfaFactorBuilder';
import oidcClientService from 'client/services/oidcClientService';
import routerService from 'client/services/routerService';
import userLoginService, { type UserForm } from 'client/services/userLoginService';

// Store
import { mapStores } from 'pinia';
import UserLoginStore from 'client/stores/UserLoginStore';

import type { MfaFactor } from 'client/types/Auth';
import type {
  SmsOtpLoginSession,
  SmsOtpLoginSessionPatch,
} from 'client/util/Constants/smsOtpLoginSession';

const { redirectTo } = Constants;
const { mfaNames } = MfaConstants;
const {
  previousMfaMethod, userLoginEmail, browserId, orgLogoUrl,
} = LocalStorageConsts;
const {
  clientType,
  clientTypeCookie,
  responseMessages,
  userConsoleRootRoute,
} = LoginConsts;
const {
  goError,
  loginError,
  mfaRequired,
  mfaUnavailable,
  passwordRequired,
} = responseMessages;

interface ControllingQueryParams {
  context?: string,
  redirectTo?: string,
}

const componentsUnderControl = [
  {
    id: '',
    componentName: EmailEntry.name as string,
  },
  {
    id: 'password',
    componentName: PasswordEntry.name as string,
  },
  {
    id: 'ssoGoContinue',
    componentName: SSOGoContinue.name as string,
  },
  {
    id: 'goRegister',
    componentName: Register.name as string,
  },
  {
    id: 'goAccess',
    componentName: Access.name as string,
  },
  {
    id: mfaNames.duo,
    componentName: DuoRedirectHandler.name as string,
  },
  {
    id: mfaNames.sms_otp,
    componentName: OtpUserLoginHandler.name as string,
  },
  {
    id: mfaNames.totp,
    componentName: TotpUserLoginHandler.name as string,
  },
  {
    id: mfaNames.webauthn,
    componentName: WebAuthnUserLoginHander.name as string,
  },
  {
    id: mfaNames.push,
    componentName: PushUserLoginHandler.name as string,
  },
];

const getCurrentComponentName = getCurrentComponentNameFromList(componentsUnderControl);

const initialState = {
  email: '',
  password: '',
  shouldRememberEmail: false,
};

type ComponentPropsType = {
  context: string,
  controllingQueryParams: ControllingQueryParams,
  hasPendingErrorMessage: boolean,
  redirectTo: string,
  successMessage: string,
  durtInfo: infoResponse,
  durtErrorMessage: string,
  onCredentialsSubmit: (credentials: UserForm) => Promise<void>,
  smsLoginSession?: SmsOtpLoginSession,
};

export default defineComponent({
  name: 'UserLoginController',

  components: {
    Access,
    AuthView,
    DuoRedirectHandler,
    ButtonLoginSwitch,
    EmailEntry,
    HelpdeskInfo,
    JumpcloudGoPageLoader,
    MfaView,
    OtpUserLoginHandler,
    PasswordEntry,
    PushUserLoginHandler,
    Register,
    SSOGoContinue,
    TotpUserLoginHandler,
    WebAuthnUserLoginHander,
  },

  props: {
    context: {
      type: String,
      default: '',
    },
    form: {
      type: Object,
      default: () => ({
        email: '',
        password: '',
      }),
    },
    paramError: {
      type: String,
      default: '',
    },
    mfaType: {
      type: String,
      default: '',
    },
    redirectTo: {
      type: String,
      default: '',
    },
    step: {
      type: String,
      default: '',
    },
    success: {
      type: String,
      default: '',
    },
  },

  setup() {
    const loginLabels = useLoginLabels();
    const mfaLabels = useMfaLabels();

    return {
      loginLabels,
      mfaLabels,
    };
  },

  data() {
    return {
      durtErrorMessage: '',
      durtExtType: '',
      durtInfo: {} as infoResponse,
      errorMessage: '',
      // using undefined so we display 0
      errorMessageDisplayDuration: undefined as number | undefined,
      factors: [] as MfaFactor[],
      isDurtExtensionPending: false,
      isDurtGetInfoValidating: false,
      isGoLoading: false,
      isLoginError: false,
      isReady: false,
      showGoContinueButton: false,
      userFormData: { ...initialState },
      googleVerifyPromise: null as Promise<string> | null,
      emailValidationPromise: null as Promise<void> | null,
      smsOtpLoginSession: createSmsOtpLoginSession(),
    };
  },

  computed: {
    ...mapStores(UserLoginStore),

    componentHandlers() {
      return {
        // Used in: Push
        clearCredentials: this.clearCredentials,
        // Used in: Email, Password, Push, WebAuthN
        resetErrorMessage: this.onResetErrorMessage,
        // Used in: Email, Password, Totp, Push, Duo
        success: this.onSuccess,
        // Used in: Email
        submitEmail: this.handleEmailSubmit,
        // Used in: Register
        goRegister: this.handleJumpCloudGoRegister,
        // Use in: Access
        goAccess: this.durtSignin,
        // Used in: SMS OTP
        'sms-session-patch': this.patchSmsOtpLoginSession,
      };
    },

    componentName(): string {
      const id = this.isMfa ? this.mfaType : this.step;

      return getCurrentComponentName(id);
    },

    componentProps(): ComponentPropsType {
      const base: ComponentPropsType = {
        context: this.context,
        // Used in: Push (TODO: see if can use handler)
        controllingQueryParams: this.controllingQueryParams,
        hasPendingErrorMessage: this.hasPendingErrorMessage,
        redirectTo: this.redirectTo,
        successMessage: this.success,
        durtInfo: this.durtInfo,
        durtErrorMessage: this.durtErrorMessage,
        onCredentialsSubmit: this.handleCredentialsSubmit,

      };

      if (this.isMfa && this.mfaType === mfaNames.sms_otp) {
        return {
          ...base,
          smsLoginSession: this.smsOtpLoginSession,
        };
      }

      return base;
    },

    /** MFA picker list; hides SMS OTP for this MFA challenge after idle terminal. */
    mfaFactorsForView(): MfaFactor[] {
      if (!this.smsOtpLoginSession.removedFromChooser) {
        return this.factors;
      }

      return this.factors.filter((f) => f.name !== mfaNames.sms_otp);
    },

    controllingQueryParams(): ControllingQueryParams {
      // this ensures we don't lose query params as we navigate
      const redirectParam = this.sanitizedRedirectTo
        ? { redirectTo: this.sanitizedRedirectTo }
        : {};
      const contextParam = this.context ? { context: this.context } : {};
      const loginHintParam = this.$route.query.login_hint
        ? { login_hint: this.$route.query.login_hint }
        : {};
      const autoGoParam = this.$route.query.autoGo
        ? { autoGo: this.$route.query.autoGo }
        : {};

      return {
        ...redirectParam,
        ...contextParam,
        ...loginHintParam,
        ...autoGoParam,
      };
    },

    errorMessageToDisplay(): string {
      // show param error on email page, local error anywhere else
      if (this.isMfa) {
        return this.errorMessage;
      }

      if (this.isGoRegistration || this.isGoAccess) {
        // let jcGO components handle UI
        return '';
      }
      return this.paramError;
    },

    hasPendingErrorMessage() {
      const remainingDuration = this.errorMessageDisplayDuration || -1;
      return remainingDuration >= 0;
    },

    headerText(): string {
      const getCredentialHeader = () => (
        this.context === 'sso' ? this.loginLabels.loginToApp : this.loginLabels.userLogin
      );

      if (this.isMfa) {
        return this.mfaLabels.verifyIdentity;
      }

      if (this.isGoRegistration || this.isGoAccess) {
        // let jcGO components handle UI
        return '';
      }

      return getCredentialHeader();
    },

    isAdminRedirectAllowed(): boolean {
      // redirectTo comes from oauth and sso login
      // these login types should block navigation to admin
      return !this.redirectTo;
    },

    isGoAccess() {
      return this.step === 'goAccess';
    },

    isGoRegistration() {
      return this.step === 'goRegister';
    },

    isSsoGoContinue() {
      return this.step === 'ssoGoContinue';
    },

    isMfa(): boolean {
      return this.step === 'mfa';
    },

    isPasswordEntry(): boolean {
      return this.step === 'password';
    },

    isHelpdesk(): boolean {
      return this.step === 'helpdesk';
    },

    sanitizedRedirectTo() {
      const isOauthOrSso = ['sso', 'oauth'].includes(this.context);
      const isUrlValid = this.redirectTo.startsWith('https://');
      // if oauth or sso, can use the redirectTo url, which has been sanitized
      // do not use redirectTo unless it is a sanitized URL
      return (isOauthOrSso && isUrlValid) ? decodeURIComponent(this.redirectTo) : '';
    },

    shouldHideEmailBadge(): boolean {
      return !this.step
        || this.isGoRegistration
        || this.isGoAccess
        || this.isSsoGoContinue
        || this.isHelpdesk;
    },

    showInvalidCredentialsError(): boolean {
      // show invalid credentials message only when
      //   its login error and not user locked out
      //   its not mfa step
      //   and there is a time query param
      const isInvalidCredentialError = !!this.$route.query.time;
      return this.isLoginError && isInvalidCredentialError && !this.isMfa;
    },

    isDurtDeviceValidatingForView(): boolean {
      return this.isDurtExtensionPending || this.isDurtGetInfoValidating;
    },

    headerLogoUrl(): string {
      // Get logo URL from emailSubmitResult if available
      const emailResult = this.userLoginStore.emailSubmitResult;
      const helpdeskContactInfo = emailResult?.helpdeskContactInfo;

      if (helpdeskContactInfo?.include_logo && helpdeskContactInfo?.logo_url) {
        return helpdeskContactInfo.logo_url;
      }
      return '';
    },
  },

  created() {
    // AUTH-7660: disable forward button, useful when coming from different routes (stepup)
    window.history.pushState({}, '');
    this.initDurt();
  },

  mounted() {
    Cookies.set(clientTypeCookie, clientType.user);
    this.validateInitialStep();
  },

  methods: {

    clearCredentials() {
      this.userLoginStore.$reset();
    },

    goToAdminLogin() {
      // removing redirectTo from session to prevent issues when switching clientTypes
      sessionStorage.removeItem(redirectTo);
      this.userFormData.email = this.userLoginStore.email;
      this.userFormData.password = this.userLoginStore.password;
      routerService.goToAdminLogin({ params: { form: this.userFormData as any } });
    },

    handleMfaRedirect(factorsRaw: MfaFactorResponse[]) {
      this.smsOtpLoginSession = createSmsOtpLoginSession();

      this.factors = mfaFactorBuilder(
        { factors: factorsRaw },
        {
          controllingQueryParams: this.controllingQueryParams,
        },
      );

      // Edge case: user with legacy MFA required setting has no
      // available factors and totp enrollment period has expired
      if (this.factors.length === 0) {
        this.handleComponentChange('', mfaUnavailable);
        return;
      }

      const mfaParam = getLastUsedFactor(this.factors);

      routerService.goToUserLogin({
        query: {
          ...this.controllingQueryParams,
          step: 'mfa',
          ...mfaParam,
        },
      });
    },

    handleComponentChange(step: string, paramError = '') {
      // append the step for everything but email page
      const stepParam = step ? { step } : {};

      routerService.goToUserLogin({
        params: { error: paramError },
        query: { ...this.controllingQueryParams, ...stepParam },
      });
    },

    handleErrorCountdown() {
      const errorCountdown = setInterval(() => {
        if ((this.errorMessageDisplayDuration as number) < 1) {
          this.onResetErrorMessage();
          clearInterval(errorCountdown);
        }

        (this.errorMessageDisplayDuration as number) -= 1;
      }, 1000);
    },

    handleNavigate() {
      if (this.userLoginStore.verifyEmailHash) {
        Util.redirect(`${userConsoleRootRoute}${this.userLoginStore.verifyEmailHash}`);
        return;
      }

      Util.redirect(this.sanitizedRedirectTo || userConsoleRootRoute);
    },

    handleRememberUserEmail() {
      const { email, shouldRememberEmail } = this.userLoginStore.$state;

      if (shouldRememberEmail) {
        LocalStorageService.setItem(userLoginEmail, email);
      }
    },

    onCredentialError(error: unknown) {
      let message = loginError;
      let factors: MfaFactorResponse[] = [];
      let correlationId = '';
      if (error instanceof RequestError) {
        message = error.message;
        correlationId = error.correlationId || '';
      } else if (error instanceof MfaRequiredError) {
        message = mfaRequired;
        factors = error.factors;
      }

      this.isLoginError = message === loginError;

      switch (message) {
        case mfaRequired:
          this.handleMfaRedirect(factors);
          break;
        case passwordRequired:
          this.handleComponentChange('password');
          break;
        default:
          // credential error displayed on current page
          routerService.replaceToUserLogin({
            params: { error: message },
            query: {
              ...this.controllingQueryParams,
              step: this.step,
              time: new Date().getTime().toString(),
              ...(correlationId ? { correlation_id: correlationId } : {}),
            },
          });
      }
    },

    patchSmsOtpLoginSession(patch: SmsOtpLoginSessionPatch) {
      const s = this.smsOtpLoginSession;

      if (patch.initialSendEverAttempted !== undefined) {
        s.initialSendEverAttempted = patch.initialSendEverAttempted;
      }

      if (patch.successfulSendIncrement) {
        s.successfulSendCount += patch.successfulSendIncrement;
      }

      if (patch.lastSessionId !== undefined) {
        s.lastSessionId = patch.lastSessionId;
      }

      if (patch.removedFromChooser !== undefined) {
        s.removedFromChooser = patch.removedFromChooser;
      }
    },

    onMfaError(error: any) {
      // Indicates invalid otp attempts > max attempts
      if (error?.status === 403) {
        this.handleComponentChange('password', error.message);
      } else {
        const smsOtpEndsSession = error instanceof RequestError
          && (error.statusCode === SMS_OTP_IDLE_TIMEOUT_STATUS_CODE
            || error.statusCode === SMS_OTP_VERIFY_RATE_LIMIT_STATUS_CODE);

        if (smsOtpEndsSession) {
          this.smsOtpLoginSession.removedFromChooser = true;
        }

        const message = error instanceof RequestError ? error.message : loginError;
        const displayDuration = error instanceof RequestError ? error.displayDuration : undefined;
        this.errorMessageDisplayDuration = displayDuration;

        if (this.errorMessageDisplayDuration && this.errorMessageDisplayDuration > 0) {
          this.handleErrorCountdown();
        }
        this.setErrorMessage(message);
      }
    },

    onResetErrorMessage() {
      this.errorMessageDisplayDuration = undefined;
      this.errorMessage = '';
      this.durtErrorMessage = '';
    },

    onSuccess() {
      if (this.isMfa && this.mfaType && this.mfaType !== mfaNames.sms_otp) {
        LocalStorageService.setItem(previousMfaMethod, this.mfaType);
      }

      this.handleRememberUserEmail();
      this.handleNavigate();
    },

    populateEmailField() {
      const savedEmail = LocalStorageService.getItem(userLoginEmail) || '';
      // email priority (highest first): userLoginStore, login_hint, localStorage
      const userEmail = this.userLoginStore.email
        || (this.$route.query.login_hint as string)
        || savedEmail;
      const shouldRememberEmail = !!(this.userLoginStore.shouldRememberEmail || savedEmail);

      this.userLoginStore.email = userEmail;
      this.userLoginStore.shouldRememberEmail = shouldRememberEmail;
    },

    validateInitialStep() {
      /**
      * Expected UI with login_hint param for each context:
      *
      * oauth: render password input.
      * sso or undefined: render populated email input.
      * with password step: submit to login services and handle from there.
      */
      const isFlowWithLoginHint = !!this.$route.query.login_hint;

      if (isFlowWithLoginHint) {
        if (this.context === 'oauth') {
          return this.validateOauthWithHint();
        }

        if (this.isPasswordEntry) {
          return this.validatePasswordWithHint();
        }
      }

      return this.validateStandard();
    },

    validateOauthWithHint() {
      this.userLoginStore.email = this.$route.query.login_hint as string;
      this.startChromeValidation();
      this.handleComponentChange('password');
    },

    validatePasswordWithHint() {
      const email = this.$route.query.login_hint as string;
      this.userLoginStore.email = email;
      this.handleEmailSubmit({ email });
    },

    validateStandard() {
      // could navigate straight to password step if saved if bookmarks without email saved
      const isInitialStepInvalid = (
        (this.isPasswordEntry || this.isMfa)
        && !this.userLoginStore.email
      );

      if (isInitialStepInvalid) {
        routerService.replaceToUserLogin({ query: { ...this.controllingQueryParams } });
      }

      this.populateEmailField();
    },

    setErrorMessage(message: string) {
      this.errorMessage = message;
    },

    async initDurt() {
      let browserIdValue = LocalStorageService.getItem(browserId);
      if (!browserIdValue) {
        browserIdValue = Util.getUniqueId();
        LocalStorageService.setItem(browserId, browserIdValue);
      }

      const spinnerDelayTimer = window.setTimeout(() => {
        this.isReady = true;
        this.isDurtExtensionPending = true;
      }, 2000);

      const hasExtensionStartTime = performance.now();
      let hasExtension = false;
      try {
        hasExtension = await durtService.hasExtension();
      } finally {
        clearTimeout(spinnerDelayTimer);
        this.isReady = true;
        // Hand off to get-info before clearing extension pending so validating state
        // never flickers false (avoids resetting the delayed loader timer).
        if (hasExtension) {
          this.isDurtGetInfoValidating = true;
        }
        this.isDurtExtensionPending = false;
      }
      const hasExtensionElapsed = performance.now() - hasExtensionStartTime;

      datadogRum.addAction('durt-hasExtension', {
        elapsed: hasExtensionElapsed,
        hasExtension,
        browserId: browserIdValue,
      });

      if (hasExtension) {
        const getInfoStartTime = performance.now();
        let getInfoCallStartTime: number | undefined;
        let infoTimeout: number | undefined;
        try {
          this.durtExtType = await durtService.getExtensionType();
          infoTimeout = await durtService.getInfoTimeout();
          getInfoCallStartTime = performance.now();
          this.durtInfo = await durtService.getInfo();
        } catch (err: any) {
          const getInfoElapsed = getInfoCallStartTime !== undefined
            ? performance.now() - getInfoCallStartTime
            : 0;
          const totalElapsed = performance.now() - getInfoStartTime;
          const durtErrorContext = {
            source: 'initDurt',
            elapsed: getInfoElapsed,
            totalElapsed,
            extensionType: this.durtExtType,
            browserId: browserIdValue,
          };

          if (getInfoElapsed && infoTimeout && getInfoElapsed >= infoTimeout) {
            datadogRum.addError(
              new Error('DURT getInfo() timeout'),
              { ...durtErrorContext, errorMessage: err?.message },
            );
          } else if (err.message !== durtApiResponseType.userNotActive) {
            datadogRum.addError(err, {
              ...durtErrorContext,
              durtInfoResult: this.durtInfo?.result,
            });
          }

          if (err.message === durtApiResponseType.userNotActive) {
            // Set result unique to Android MDT flow
            this.durtInfo.result = err.message;
          }
          // Ignoring error for now, and just pretend they can't DURT
        } finally {
          this.isDurtGetInfoValidating = false;
        }

        const isLoginServiceOauth = this.context === 'oauth';

        // desktop registration flow
        // only show goRegistration when user is not doing oauth
        // which includes linked identities and go-registration
        if (this.durtInfo.result === durtApiResponseType.userNotRegisteredError
          && !isLoginServiceOauth) {
          // Param error can come from failed OIDC flow during registration
          this.handleComponentChange('goRegister', this.paramError);
        }

        // standard desktop access flow
        // Note: Password step is handled by early return above
        if (this.durtInfo.result === durtApiResponseType.success) {
          this.startEmailValidation(this.durtInfo.email as string);
          this.handleComponentChange('goAccess');
        }

        // android access flow
        if (this.durtInfo.result === durtApiResponseType.userNotActive
          && this.durtExtType === extensionTypes.androidHttp
          && !isLoginServiceOauth
        ) {
          this.handleComponentChange('goAccess');
        }
      }
    },

    startChromeValidation() {
      if (this.googleVerifyPromise) return;
      try {
        this.googleVerifyPromise = managedBrowserService.validateChrome();
      } catch (error) {
        // do nothing
      }
    },

    async waitForChromeValidation() {
      if (!this.googleVerifyPromise) {
        this.startChromeValidation();
      }

      try {
        await this.googleVerifyPromise;
      } catch (error) {
        // Clear the cached promise so subsequent attempts retry instead of failing instantly
        this.googleVerifyPromise = null;
      }
    },

    startEmailValidation(email:string) {
      this.emailValidationPromise = userLoginService.submitEmail({ email });
    },

    async waitForEmailValidation(email:string) {
      if (!this.emailValidationPromise) {
        this.startEmailValidation(email);
      }
      await this.emailValidationPromise;
    },

    async handleCredentialsSubmit(credentials: UserForm) : Promise<void> {
      await this.waitForChromeValidation();
      await userLoginService.submitCredentials(credentials);
    },

    async handleEmailSubmit(payload: { email: string, redirectTo?: string }) {
      // USER_NOT_ACTIVE response is unique to Android MDT flow
      if (this.durtInfo.result === durtApiResponseType.userNotActive
        && this.durtExtType === extensionTypes.android
      ) {
        try {
          const accessData = await durtService.getAccess();
          await durtService.signin(accessData);

          if (this.context === 'sso') {
            this.handleComponentChange('ssoGoContinue');
            return;
          }

          Util.redirect(this.sanitizedRedirectTo || userConsoleRootRoute);
        } catch {
          // continue to submit email and show password field
        }
      }

      try {
        /**
         * Start Chrome validation before email submission to prevent interruption by
         * federated redirects.
         * Context: Federated users trigger page reloads after email submission,
         * which would destroy the Chrome validation iframe and prevent the managed browser
         * API call from completing. Starting validation first ensures it completes before
         * any redirects occur.
        */
        this.startChromeValidation();
        await this.waitForChromeValidation();

        const result: any = await userLoginService.submitEmail(payload);
        // Store only result.data in the store
        this.userLoginStore.emailSubmitResult = result.data || {};
        const helpdeskInfo = result.data?.helpdeskContactInfo;
        if (helpdeskInfo?.include_logo) {
          if (helpdeskInfo?.logo_url) {
            LocalStorageService.setItem(orgLogoUrl, helpdeskInfo.logo_url);
          } else {
            LocalStorageService.removeItem(orgLogoUrl);
          }
        }

        await api.user.validateDeviceCert().catch(() => {});
        this.onResetErrorMessage();
        this.handleComponentChange('password');
      } catch {
        this.onCredentialError(new RequestError(0, passwordRequired));
      }
    },

    async handleJumpCloudGoRegister() {
      try {
        await oidcClientService.signin(window.location.search.slice(1), this.durtInfo.console_env);
      } catch (err: any) {
        this.durtErrorMessage = goError;
      }
    },

    async durtSignin() {
      const durtInfoEmail = this.durtInfo.email as string;
      try {
        this.isGoLoading = true;

        await this.waitForEmailValidation(durtInfoEmail);

        await this.waitForChromeValidation();
        const accessData = await durtService.getAccess();

        try {
          // Wait 2s to allow JCGO animation enough time to render
          await Promise.all([durtService.signin(accessData), Util.delay(2000)]);

          // If we are doing SSO with Android, then we need to show a continue button
          // to get user interaction in case the SSO redirect is using an App-Link
          if (this.context === 'sso' && this.durtExtType === extensionTypes.androidHttp) {
            // accessData.session is a JWT token
            const dust = JSON.parse(atob(accessData.session.split('.')[1]));
            this.durtInfo.email = dust?.user?.email;
            this.showGoContinueButton = true;
            return;
          }

          Util.redirect(this.sanitizedRedirectTo || userConsoleRootRoute);
        } catch (signinErr: any) {
          // Check if MFA is required
          const { data } = get(signinErr, 'response', {});
          // Enhanced logging to debug the structure of the error response
          const errorMessage = get(data, 'error.message');
          const altErrorMessage = get(data, 'message');

          if (errorMessage === responseMessages.mfaRequired
            || altErrorMessage === responseMessages.mfaRequired) {
            // Handle MFA required case

            // Make sure the email from durtInfo is set in userLoginStore
            // This is critical for EmailBadge to show the correct email during MFA flow
            if (this.durtInfo && this.durtInfo.email) {
              this.userLoginStore.email = this.durtInfo.email as string;
            }
            // Try different paths for factors
            const factors = get(data, 'factors', []);
            const altFactors = get(data, 'error.factors', []);
            const finalFactors = factors.length > 0 ? factors : altFactors;

            this.isGoLoading = false; // Hide the loader
            this.handleMfaRedirect(finalFactors); // Redirect to MFA flow
            return;
          }
          // Re-throw for other errors
          throw signinErr;
        }
      } catch (err: any) {
        const durtApiResponseValues = Object.values(durtApiResponseType);

        const parseError = (e: any): string => {
          if (durtApiResponseValues.includes(err.message)) {
            if (err.message === durtApiResponseType.userNotActive) {
              return durtApiResponseType.userNotActive;
            }
            return goError;
          }
          const data = get(e, 'response.data', {});
          const message = get(data, 'message', loginError);
          try {
            const { text } = JSON.parse(message);
            return text;
          } catch {
            // This is required to handle the custom message error for jc go
            if (Util.isCustomErrorMessage(data)) {
              return JSON.stringify(data);
            }
            return message;
          }
        };

        this.durtErrorMessage = parseError(err);

        datadogRum.addError(err, {
          timestamp: Date.now(),
          env: Util.getEnvironment(),
          errorMessage: err.message,
          durtErrorMessage: this.durtErrorMessage,
          browserId: LocalStorageService.getItem(browserId),
          source: 'durtSignin',
          hint: 'Error while signing in with JCGO',
          org: Util.getDomainFromEmail(durtInfoEmail),
          email: Util.maskEmail(durtInfoEmail),
        });

        this.isGoLoading = false;
      }
    },
  },
});
